import { hasDuplicates } from 'utils/duplicates';
import { CommonPagesI } from 'store/models/file-selected/CommonPages';
import { FileSelected } from 'store/models/file-selected/FileSelected';
import { ApiAttachment, MainFiles } from 'store/models/MainFiles';
import Booking, {
  initialWorkingBooking,
  BookingI,
  BookingNoErrorStatusI,
} from 'store/models/bookings/Booking';
import { BookingIdToFoundMapI } from 'store/models/bookings/bookings.interface';
import { EachPage } from 'store/models/file-selected/file-selected.interface';
import { isEmpty } from 'lodash';

class Bookings {
  bookingMs: Booking[];
  returnModel: boolean;
  constructor(
    formattedBookings: BookingI[] | undefined = [],
    returnModel = false,
  ) {
    this.bookingMs = formattedBookings.map((b) => new Booking(b));
    this.returnModel = returnModel;
  }

  isSingle(): boolean {
    return this.length === 1;
  }

  getRelavantFoundMap(
    foundMap: BookingIdToFoundMapI,
  ): BookingIdToFoundMapI {
    return Object.keys(foundMap)
      .filter((no) => this.bookingNos.includes(no))
      .reduce((acc, no) => ({ ...acc, [no]: foundMap[no] }), {});
  }

  isAllFoundAndNotCanceled(foundMap: BookingIdToFoundMapI): boolean {
    foundMap = this.getRelavantFoundMap(foundMap);
    return (
      Object.keys(foundMap).length > 0 &&
      Object.values(foundMap).every(
        (item) => item && item.found && item.notCanceled,
      )
    );
  }

  /**
   * add error or clear for all booking nos
   * @param foundMap
   */
  validateFoundAndNotCanceled(
    foundMap: BookingIdToFoundMapI,
  ): boolean {
    foundMap = this.getRelavantFoundMap(foundMap);

    let pass = true;
    const newBookingsM = this.bookingMs.map((bookingM, index) => {
      bookingM = bookingM.validateFoundAndNotCanceled(foundMap);
      if (!bookingM.passFoundNoCancel) pass = false;
      return bookingM;
    });

    console.debug(`newBookingsM: `, newBookingsM, foundMap);

    this.bookingMs = newBookingsM;
    return pass;
  }
  /**
   * TODO: move this or _validateFoundAndNotCanceledAtIndex to Booking model and call validate on that
   * @param index
   * @param foundMap
   */
  validateFoundAndNotCanceledAtIndex(
    index: number,
    foundMap: BookingIdToFoundMapI,
  ): boolean {
    if (index >= this.length) throw Error(`invalid index ${index}`);
    foundMap = this.getRelavantFoundMap(foundMap); // QUESTION?: do we need to just get the relavant?
    let booking = this.getBookingAtIndex(index);

    booking = booking.validateFoundAndNotCanceled(foundMap);
    return booking.passFoundNoCancel;
  }

  static getInitCurBookings(
    bookings: BookingI[] | undefined,
    attachments?: ApiAttachment[],
  ): BookingI[] {
    if (!bookings) return [];
    return bookings.map((booking) => ({
      ...booking,
      fileSelected: FileSelected.formatForInitCurBookings(
        booking.fileSelected,
        attachments,
      ),
    }));
  }

  hasEmptyOrDuplicates(): boolean {
    if (!this.length) return true; // true for being empty
    const result =
      this.hasAnyEmptyBookingNos() || this.hasDuplicatedBookingNos();
    return result;
  }

  getIndexOfBookingNo(bookingNo: string) {
    return this.bookingNos.findIndex((bn) => bn === bookingNo);
  }

  /**
   * check if the new one would match the old ones
   * @param bookingNo
   */
  isBookingNoMatchExisting(bookingNo: string) {
    return hasDuplicates(this.bookingNos.concat(bookingNo));
  }

  /**
   * for the existing
   */
  hasDuplicatedBookingNos() {
    return hasDuplicates(this.bookingNos);
  }

  hasDuplicateBookingNoWithIndex(index, bookingNo?): boolean {
    if (index < 0 || index >= this.length)
      throw Error(`invalid index ${index}`);
    if (!bookingNo) {
      bookingNo = this.getBookingAtIndex(index).bookingNo;
    }

    const ignoreIndex = this.bookingNos.filter((_, i) => i !== index);
    const result = ignoreIndex.includes(bookingNo);
    // console.debug(`hasDuplicateBookingNoWithIndex: `, {
    //   result,
    //   ignoreIndex,
    //   index,
    // });
    return result;
  }

  indexesHasDuplicateBookingNoWithIndex(index, bookingNo?): number[] {
    if (index < 0 || index >= this.length)
      throw Error(`invalid index ${index}`);
    if (!bookingNo) {
      bookingNo = this.getBookingAtIndex(index).bookingNo;
    }
    return this.bookingNos
      .map((bn, i) =>
        i !== index && !isEmpty(bn) && bn === bookingNo
          ? i
          : undefined,
      )
      .filter((bn) => bn !== undefined) as number[];
  }

  get bookingNos() {
    return this.bookingMs.map((bookingM) => bookingM.bookingNo);
  }

  getFirstBookingNo(): string | undefined {
    return this.length > 0 ? this.bookingNos[0] : undefined;
  }

  getBookingNoAtIndex(index): string | undefined {
    if (index < 0) return undefined;
    if (index < this.length) {
      return this.bookingNos[index];
    }
    return undefined;
  }

  static formToAutosaveApi(bookings) {
    // TODO: format bookings in Bookings.formToAutosaveApi?
    return bookings;
  }

  /**
   * see if selected files are exactly the same as original attachments (same pages)
   * @param partIdPageNums
   * @param partId
   * @param fPages
   * @returns boolean
   */
  static _compareFileSelectedToOriAttachments(
    partIdPageNums: { [partId: string]: number[] },
    partId: string | undefined,
    fPages: EachPage[],
  ): boolean {
    if (!partId) return false; // splitted files will not have partId
    if (!(partId in partIdPageNums)) return false; // shouldn't happen. TODO: double check or throw error
    const oriPages = partIdPageNums[partId];
    // if pages is empty array, the below condition will still be true
    return (
      oriPages.length === fPages.length &&
      fPages.every((fP, i) => fP.pageNo === oriPages[i])
    );
  }

  /**
   * TODO: format the whole file data
   * @param bookings
   */
  static formToSubmitBookingApi(
    bookings: BookingI[] | undefined,
    attachments: ApiAttachment[],
  ) {
    const partIdPageNums = MainFiles.getPartIdToPageNumsMap(
      attachments,
    );
    console.debug(
      'formToSubmitBookingApi -> ',
      `attachments: `,
      attachments,
      'partIdPageNums: ',
      partIdPageNums,
    );

    return (bookings || []).map((b) => ({
      bookingNo: b.bookingNo,
      files: (b.fileSelected || []).map((f) => {
        let result: {
          filename: string;
          partId?: string;
          pages?: { partId: string; num: number }[] | null;
        } = {
          filename: f.filename,
        };
        const matched = Bookings._compareFileSelectedToOriAttachments(
          partIdPageNums,
          f.partId,
          f.pages,
        );
        if (matched) {
          result['partId'] = f.partId;
        } else if ('pages' in f) {
          // if selected pages are not exactly the same as ori.att
          // and if ori.att has pages
          result['pages'] = f.pages.map((p) => ({
            partId: p.partId,
            num: p.pageNo,
          }));
          // if pages is empty array, use null
          if (!result['pages'].length) {
            result['pages'] = null;
          }
        }
        return result;
      }),
    }));
  }
  public get bookings() {
    return this.bookingMs.map((b) => b.booking);
  }
  getIndexesOfAllEmptyBookingNos(): number[] {
    return this.bookingMs
      .map((b, i) => (b.isBookingNoEmpty() ? i : null))
      .filter((i) => i !== null) as number[];
  }
  hasAnyEmptyBookingNos(): boolean {
    return this.getIndexesOfAllEmptyBookingNos().length > 0;
  }

  markHasErrorToIndexes(
    status: BookingNoErrorStatusI,
    indexes: number[],
  ): BookingI[] | Bookings {
    this.bookingMs = this.bookingMs.map((b, i) =>
      indexes.includes(i) ? b.markHasError(status) : b,
    );
    return this.returnModel ? this : this.bookings;
  }
  getLastBookingNo(): string {
    let lastBookingNo = '';
    if (this.bookings.length) {
      const lastI = this.bookingMs.length - 1;
      if (!this.isBookingNoEmptyAtIndex(lastI)) {
        lastBookingNo = this.bookings[lastI].bookingNo;
      }
    }
    return lastBookingNo;
  }

  getAutoIncrBookingNoOfLastBookingNo() {
    return this.getAutoIncrBookingNo(this.getLastBookingNo(), 1);
  }

  /**
   * if previous is empty string -> return empty string
   * otherwise increment from the previous
   * @param srcBookingNo
   * @param plusNum
   */
  getAutoIncrBookingNo(srcBookingNo: string, plusNum = 1): string {
    console.debug(`srcBookingNo: ${srcBookingNo}`);
    let result = '1';
    srcBookingNo = srcBookingNo.trim();
    if (srcBookingNo) {
      const regex = /([\d]+$)/g;
      const matchedArr = srcBookingNo.match(regex);
      console.debug(`matchedArr: `, matchedArr);
      if (matchedArr) {
        const matchedAr = matchedArr.filter(
          (m) => m !== undefined && m !== null && m !== '',
        );
        const matched =
          matchedAr &&
          matchedAr.length > 0 &&
          matchedAr[matchedAr.length - 1];
        console.debug(`matched: `, matched, 'ori', srcBookingNo);
        if (matched) {
          const nDigit = matched.length; // count digit for padding zero
          const parsed = parseInt(matched);
          if (parsed !== undefined && !isNaN(parsed)) {
            result = `${parsed + plusNum}`.padStart(nDigit, '0'); // pad zero at the start
            result = srcBookingNo.replace(regex, result);
          }
        }
      }
    }
    return result;
  }

  addNewBookingsByNum(
    num: number | string,
    replaceOldBookings = false,
    attachments: ApiAttachment[],
    commonPages: CommonPagesI,
    formDataBookingNo?: string,
    isSplit?: boolean,
    isSelectedAll?: boolean,
  ) {
    num = typeof num === 'string' ? parseInt(num) : num;
    const ori = replaceOldBookings ? [] : this.bookingMs;
    let lastI = this.bookings.length
      ? this.bookingMs.length - 1
      : undefined;
    let lastBookingNo;

    if (lastI !== undefined && !this.isBookingNoEmptyAtIndex(lastI)) {
      lastBookingNo = this.bookings[lastI].bookingNo;
    } else {
      // if last booking no is empty or never had booking no before
      lastBookingNo = '0';
    }

    let fileSelectedM = new FileSelected(
      initialWorkingBooking.fileSelected,
      true,
    ).appendManySrcFiles(
      attachments,
      false, // not sure if should pass false in here
    ) as FileSelected;
    fileSelectedM = fileSelectedM.addCommonPagesToAllDestFiles(
      commonPages,
    ) as FileSelected;

    for (let i = 1; i <= num; i++) {
      let newBookingNo: string;
      if (!ori.length && i === 1) {
        newBookingNo = Bookings.getDefaultFirstBookingNo(
          formDataBookingNo,
          isSplit,
        );
      } else {
        newBookingNo = isSplit
          ? this.getAutoIncrBookingNo(lastBookingNo)
          : ''; // non-split should be all blanks
      }

      ori.push(
        new Booking({
          ...initialWorkingBooking,
          fileSelected: fileSelectedM.fileSelected,
          bookingNo: newBookingNo,
          isSelected: isSelectedAll ?? false,
        }),
      );
      lastBookingNo = newBookingNo;
    }
    this.bookingMs = ori;

    return this.returnModel ? this : this.bookings;
  }
  /**
   * add bookings with many bookings nos
   * TODO: write test case for this function
   * @param bookingNos
   */
  addNewBookings(
    bookingNos: string[],
    replaceOldBookings = false,
    attachments: ApiAttachment[], // use attachments by default
    commonPages: CommonPagesI,
    isSelectedAll?: boolean,
  ) {
    if (bookingNos.length) {
      this.bookingMs = (replaceOldBookings
        ? []
        : this.bookingMs
      ).concat(
        bookingNos.map((bN, i) => {
          const bToUse = initialWorkingBooking;
          let fileSelectedM = new FileSelected(
            bToUse.fileSelected,
            true,
          ).appendManySrcFiles(
            attachments,
            false, // NOTE: not sure if should pass false here
          ) as FileSelected;
          fileSelectedM = fileSelectedM.addCommonPagesToAllDestFiles(
            commonPages,
          ) as FileSelected;
          return new Booking({
            // take data from the old empty one and add to the new booking
            ...bToUse,
            fileSelected: fileSelectedM.fileSelected,
            bookingNo: bN,
            isSelected: isSelectedAll ?? false,
          });
        }),
      );
    }

    return this.returnModel ? this : this.bookings;
  }

  static getDefaultFirstBookingNo(
    formTicketBookingNo?: string,
    isSplit?: boolean,
  ): string {
    return formTicketBookingNo || (isSplit ? '1' : '');
  }

  /**
   * for creating the first ticket, use the booking no from the ticket
   * add new booking
   * addNewBooking
   * @param statusToClear
   * @param autoSelectNew
   * @param attachments
   * @param formTicketBookingNo
   */
  appendNewBooking(
    attachments: ApiAttachment[],
    commonPages: CommonPagesI,
    formTicketBookingNo?: string,
    isSplit?: boolean,
    isSelectedAll?: boolean,
  ) {
    let newBookingNo;
    if (!this.bookingMs.length) {
      newBookingNo = Bookings.getDefaultFirstBookingNo(
        formTicketBookingNo,
        isSplit,
      );
    } else {
      newBookingNo = isSplit
        ? this.getAutoIncrBookingNoOfLastBookingNo()
        : '';
    }

    return this.addNewBookings(
      [newBookingNo],
      false,
      attachments,
      commonPages,
      isSelectedAll,
    );
  }

  clearAllErrorsAtAllIndexes() {
    this.bookingMs = this.bookingMs.map((b, i) => b.clearAllErrors());
    return this.returnModel ? this : this.bookings;
  }
  clearErrorsAtAllIndexes(statusesToClear: BookingNoErrorStatusI[]) {
    this.bookingMs = this.bookingMs.map((b, i) =>
      b.clearErrors(statusesToClear),
    );
    return this.returnModel ? this : this.bookings;
  }

  clearErrorAtIndex(
    statusToClear: BookingNoErrorStatusI,
    index: number,
  ): Bookings | BookingI[] {
    this.bookingMs = this.bookingMs.map((b, i) =>
      i === index ? b.clearError(statusToClear) : b,
    );
    return this.returnModel ? this : this.bookings;
  }

  clearErrorsAtIndex(
    statusesToClear: BookingNoErrorStatusI[],
    index: number,
  ): Bookings | BookingI[] {
    this.bookingMs = this.bookingMs.map((b, i) =>
      i === index ? b.clearErrors(statusesToClear) : b,
    );
    return this.returnModel ? this : this.bookings;
  }
  clearErrorAtIndexes(
    statusToClear: BookingNoErrorStatusI,
    indexes: number[],
  ): Bookings | BookingI[] {
    this.bookingMs = this.bookingMs.map((b, i) =>
      indexes.includes(i) ? b.clearError(statusToClear) : b,
    );
    return this.returnModel ? this : this.bookings;
  }

  clearErrorAtAllIndexes(
    statusToClear: BookingNoErrorStatusI,
  ): Bookings | BookingI[] {
    this.bookingMs = this.bookingMs.map((b, i) =>
      b.clearError(statusToClear),
    );
    return this.returnModel ? this : this.bookings;
  }
  isBookingNoEmptyAtIndex(index: number) {
    if (index > this.bookingMs.length - 1)
      return Error('invalid index');
    return this.bookingMs[index].isBookingNoEmpty();
  }

  replaceBookingMAtIndex(index: number, bookingM: Booking) {
    this.bookingMs = this.bookingMs.map((m, i) =>
      i !== index ? m : bookingM,
    );
    return this.returnModel ? this : this.bookings;
  }

  /**
   * only works with the returnModel mode
   * @param index
   * @param skipClearErrorAtAllIndexes
   * @param foundMap
   */
  validateBookingNoAtIndexAndAddErrorStatus(
    index: number,
    skipClearErrorAtAllIndexes = false,
    oldBookingNoAtIndex?: string,
    foundMap?: BookingIdToFoundMapI,
  ): Bookings {
    if (!this.returnModel)
      throw Error(`only works with the returnModel mode`);
    const bookingM = this.getBookingAtIndex(
      index,
    ).validateBookingNoAndAddErrorStatus(undefined, foundMap);

    this.replaceBookingMAtIndex(index, bookingM);

    // const hasDup = this.hasDuplicatedBookingNos();

    // the indexes that match this one (exclude this index)
    const hasDupIndexes = this.indexesHasDuplicateBookingNoWithIndex(
      index,
    );

    if (hasDupIndexes.length) {
      this.markHasErrorToIndexes(
        'bookingNoIsDup',
        hasDupIndexes.concat([index]),
      );
    } else if (!skipClearErrorAtAllIndexes) {
      // clear error here and the error at indexes used to match this one
      // clear hasDup for all that used to be == oldBookingNoAtIndex
      const toClearIndexes = this.bookingNos
        .map((bn, i) => (bn === oldBookingNoAtIndex ? i : undefined))
        .filter((bn) => bn !== undefined)
        .concat([index]) as number[];
      this.clearErrorAtIndexes('bookingNoIsDup', toClearIndexes);
    }

    // if (foundMap)
    //   this.validateFoundAndNotCanceledAtIndex(index, foundMap);

    return this;
  }

  validateAllBookingsAddErrorStatus(
    skipClearErrorAtAllIndexes = true,
    foundMap?: BookingIdToFoundMapI,
  ) {
    if (!this.returnModel)
      throw Error(`only works with the returnModel mode`);
    this.bookings.forEach((b, i) => {
      // do not pass found map
      // just check at the end
      this.validateBookingNoAtIndexAndAddErrorStatus(
        i,
        skipClearErrorAtAllIndexes,
      );
    });

    if (foundMap) this.validateFoundAndNotCanceled(foundMap);

    return this;
  }

  get length(): number {
    return this.bookingMs.length;
  }

  getBookingAtIndex(index: number): Booking {
    if (index > this.length - 1)
      throw Error(`invalid index passed (${index})`);
    return this.bookingMs[index];
  }

  removeBookingsAtIndexs(indexes: number[]): BookingI[] | Bookings {
    this.bookingMs = this.bookingMs.filter(
      (b, i) => !indexes.includes(i),
    );
    // clear duplicate because when delete one row it's possible that the row deleted was the duplicated one
    this.validateAllBookingsAddErrorStatus(false);
    return this.returnModel ? this : this.bookings;
  }
  updateBookingNoAtIndex(value, index): BookingI[] | Bookings {
    this.bookingMs = this.bookingMs.map((booking, i) =>
      i !== index ? booking : booking.updateBookingNo(value),
    );
    return this.returnModel ? this : this.bookings;
  }

  isAllSelected(): boolean {
    return (
      this.length > 0 && this.bookingMs.every((b) => b.isSelected)
    );
  }

  markAllSelected(val = true): BookingI[] | Bookings {
    this.bookingMs = this.bookingMs.map(
      (b) => b.markIsSelected(val, true) as Booking,
    );
    return this.returnModel ? this : this.bookings;
  }

  markIsSelectedAtIndex(
    index: number,
    val = true,
  ): BookingI[] | Bookings {
    this.bookingMs = this.bookingMs.map((b, i) =>
      i !== index ? b : (b.markIsSelected(val, true) as Booking),
    );
    return this.returnModel ? this : this.bookings;
  }

  removeSelectedBookings(): BookingI[] | Bookings {
    this.bookingMs = this.bookingMs.filter((b) => !b.isSelected);
    return this.returnModel ? this : this.bookings;
  }

  removeFilesFromAllBookings(
    partIds: string[],
  ): BookingI[] | Bookings {
    this.bookingMs = this.bookingMs.map((bM) =>
      bM.removeFiles(partIds),
    );
    return this.returnModel ? this : this.bookings;
  }
}

export default Bookings;
